if (!customElements.get("tax-exempt-manager")) {
  /**
   * @class TaxExemptManager
   * @extends {HTMLElement}
   * Manages VAT ID validation and submission on the customer account page.
   * It handles user input, communicates with a backend API to validate VAT numbers,
   * and updates the UI to reflect the validation status.
   */
  class TaxExemptManager extends HTMLElement {
    //
    // ----------------- STATIC PROPERTIES -----------------
    //

    static Selectors = {
      INPUT_FIELD: "[data-vat-id-input]",
      HIDDEN_INPUT_FIELD: "[data-vat-id-hidden-input]",
      SAVE_BUTTON: "[data-save-button]",
      UNSET_BUTTON: "[data-unset-button]",
      CONFIG: "[data-config]",
      INPUT_ICON: "[data-input-icon]",
      MESSAGE: "[data-message]",
    };

    static ErrorCodes = {
      VAT_DOES_NOT_EXIST: "VAT_DOES_NOT_EXIST",
      VAT_NOT_VALID: "VAT_NOT_VALID",
      UNSET_ERROR: "UNSET_ERROR",
      SOMETHING_WENT_WRONG: "SOMETHING_WENT_WRONG",
    };

    //
    // ----------------- INSTANCE PROPERTIES -----------------
    //

    /** @private */
    _checkmarkSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="#008000" d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z" /></svg>`;
    /** @private */
    _redmarkSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="5 5 30 30"><path stroke="#b22222" stroke-width="4" d="M 10,10 L 30,30 M 30,10 L 10,30" /></svg>`;

    //
    // ----------------- CONSTRUCTOR & LIFECYCLE -----------------
    //

    /**
     * @constructor
     */
    constructor() {
      super();
      /**
       * Master switch for all logging. Set to `false` to disable all console output.
       * @private
       * @type {boolean}
       */
      this._loggingEnabled = true;
    }

    /**
     * Called when the element is added to the DOM.
     * Initializes event listeners and sets up page-specific logic.
     */
    connectedCallback() {
      this._log("INFO", "Component connected and initialized.");
      this._log("INFO", "Configuration", this._configData);

      if (this._saveButton) {
        this._saveButton.addEventListener("click", this._handleSaveClick);
      } else {
        this._log("ERROR", "Save button not found");
      }

      if (this._unsetButton) {
        this._unsetButton.addEventListener("click", this._handleUnsetClick);
      } else {
        this._log("WARNING", "Unset button not found");
      }
    }

    /**
     * Called when the element is removed from the DOM.
     * Cleans up event listeners to prevent memory leaks.
     */
    disconnectedCallback() {
      this._log("INFO", "Component disconnected. Cleaning up listeners.");

      if (this._saveButton) {
        this._saveButton.removeEventListener("click", this._handleSaveClick);
      }
      if (this._unsetButton) {
        this._unsetButton.removeEventListener("click", this._handleUnsetClick);
      }
    }

    //
    // ----------------- DOM GETTERS -----------------
    //

    get _configData() {
      const dataScript = this._config;
      return dataScript ? JSON.parse(dataScript.text) : null;
    }

    get _saveButton() {
      return this.querySelector(TaxExemptManager.Selectors.SAVE_BUTTON);
    }
    get _unsetButton() {
      return this.querySelector(TaxExemptManager.Selectors.UNSET_BUTTON);
    }
    get _inputField() {
      return this.querySelector(TaxExemptManager.Selectors.INPUT_FIELD);
    }
    get _hiddenInputField() {
      return this.querySelector(TaxExemptManager.Selectors.HIDDEN_INPUT_FIELD);
    }
    get _config() {
      return this.querySelector(TaxExemptManager.Selectors.CONFIG);
    }
    get _inputIcon() {
      return this.querySelector(TaxExemptManager.Selectors.INPUT_ICON);
    }
    get _message() {
      return this.querySelector(TaxExemptManager.Selectors.MESSAGE);
    }

    //
    // ----------------- EVENT HANDLERS -----------------
    //

    /**
     * Handles the click event on the "Save" or "Update" button.
     * Validates the entered VAT ID and stores it if valid.
     * @private
     * @param {MouseEvent} e - The click event object.
     */
    _handleSaveClick = async (e) => {
      e.preventDefault();
      this._resetUi();
      this._log("INFO", "Save button clicked.");
      this._toggleButtonLoading(this._saveButton, true);

      const value = this._inputField.value.replace(/\s/g, "").toUpperCase();
      const { error } = await this._validateAndStoreVatID(value);

      if (error) {
        this._handleError(error);
      } else {
        this._handleSuccess(this._configData.strings.vat_id_added);

        if (this._unsetButton) {
          this._log("INFO", "Removing Unset button disabled status");
          this._unsetButton.removeAttribute("disabled");
        }

        this._updateHiddenInput(value);
      }

      this._toggleButtonLoading(this._saveButton, false);
    };

    /**
     * Handles the click event on the "Delete" button.
     * Removes the VAT ID from the customer's account.
     * @private
     * @param {MouseEvent} e - The click event object.
     */
    _handleUnsetClick = async (e) => {
      e.preventDefault();
      this._resetUi();
      this._log("INFO", "Unset button clicked.");
      this._toggleButtonLoading(this._unsetButton, true);

      const { error, success } = await this._unsetCustomerVat();

      if (error) {
        this._handleError(TaxExemptManager.ErrorCodes.UNSET_ERROR);
      } else {
        this._handleSuccess(this._configData.strings.vat_deleted);
        this._inputField.value = "";
        this._updateHiddenInput("");
      }

      this._toggleButtonLoading(this._unsetButton, false);

      if (success) {
        this._log("INFO", "Setting Unset button to disabled");
        this._unsetButton.setAttribute("disabled", "");
      }
    };

    //
    // ----------------- CORE LOGIC & API -----------------
    //

    /**
     * Validates a VAT ID and, if successful, stores it on the customer record.
     * @private
     * @param {string} vatId - The VAT ID to validate and store.
     * @returns {Promise<{success?: boolean, error?: string}>} An object indicating success or containing an error code.
     */
    async _validateAndStoreVatID(vatId) {
      const payload = { vat: { vat_id: vatId } };
      const { shop, app } = this._configData;
      this._log("INFO", `Validating VAT ID:`, payload);

      try {
        const data = await this._fetchAPI(
          "POST",
          `${app.url}/api/v1/vat/validate?shopify_domain=${shop.domain}`,
          payload
        );

        if (!data.vat.exists) {
          this._log("WARNING", "Vat ID does not exist");
          return { error: TaxExemptManager.ErrorCodes.VAT_DOES_NOT_EXIST };
        }

        if (!data.vat.valid) {
          this._log("WARNING", "Vat ID is not valid");
          return { error: TaxExemptManager.ErrorCodes.VAT_NOT_VALID };
        }

        if (this._configData.customer.id !== '') {
          await this._setCustomerVat(vatId);
        }
        
        return { success: true };
      } catch (error) {
        this._log("ERROR", "Run into an issue Validating Vat ID", error);
        return { error: TaxExemptManager.ErrorCodes.SOMETHING_WENT_WRONG };
      }
    }

    /**
     * Sends a request to save the VAT ID to the customer object.
     * @private
     * @param {string} vatId - The valid VAT ID to save.
     */
    _setCustomerVat = async (vatId) => {
      this._log("INFO", `Setting customer VAT ID to "${vatId}".`);
      const { customer, shop, app } = this._configData;
      const payload = {
        customer: {
          vat_id: vatId,
          customer_id: customer.id,
          shopify_domain: shop.domain,
        },
      };

      try {
        await this._fetchAPI("POST", `${app.url}/api/v1/customer/set`, payload);
        this._log("INFO", "Customer Vat ID updated");
      } catch (error) {
        this._log("ERROR", "Something went wrong updating Customer Vat ID");
      }
    };

    /**
     * Sends a request to remove the VAT ID from the customer object.
     * @private
     * @returns {Promise<{success?: boolean, error?: boolean}>} An object indicating success or failure.
     */
    _unsetCustomerVat = async () => {
      this._log("INFO", "Unsetting customer VAT ID.");
      const { customer, shop, app } = this._configData;
      const payload = {
        customer: { customer_id: customer.id, shopify_domain: shop.domain },
      };

      try {
        await this._fetchAPI(
          "POST",
          `${app.url}/api/v1/customer/unset`,
          payload
        );
        this._log("INFO", "Customer Vat ID unset");
        return { success: true };
      } catch (error) {
        this._log("ERROR", "Something went wrong unsetting Customer Vat ID");
        return { error: true };
      }
    };

    /**
     * Makes an asynchronous HTTP request using the Fetch API.
     * @private
     * @async
     * @param {string} method - The HTTP method (e.g., 'POST').
     * @param {string} endpoint - The URL to send the request to.
     * @param {object} [payload=null] - The request payload.
     * @returns {Promise<object|null>} A promise that resolves with the parsed JSON response or null for empty responses.
     */
    _fetchAPI = async (method, endpoint, payload = null) => {
      this._log("INFO", `Fetching [${method}]`, endpoint);
      if (payload) {
        this._log("INFO", "Payload:", payload);
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      try {
        const response = await fetch(endpoint, {
          method: method.toUpperCase(),
          headers: { "Content-Type": "application/json" },
          body: payload ? JSON.stringify(payload) : null,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const responseText = await response.text();
        if (!responseText) {
          this._log("WARNING", "API Response was empty.");
          return null;
        }

        const data = JSON.parse(responseText);
        this._log("INFO", "API Response:", data);
        return data;
      } catch (error) {
        this._log("ERROR", "Fetch API Error:", error.message);
        clearTimeout(timeoutId);
        throw error;
      }
    };

    //
    // ----------------- UI & RENDER HELPERS -----------------
    //

    /**
     * Resets the UI by clearing messages and button loading states.
     * @private
     */
    _resetUi() {
      this._clearMessage();
      this._toggleButtonLoading(this._saveButton, false);
      this._toggleButtonLoading(this._deleteButton, false);
    }

    /**
     * Updates the UI to show a success state.
     * @private
     * @param {string} message - The success message to display.
     */
    _handleSuccess(message) {
      this._inputIcon.innerHTML = this._checkmarkSvg;
      this._renderMessage(message, "SUCCESS");
    }

    /**
     * Updates the UI to show an error state based on an error code.
     * @private
     * @param {string} errorCode - The error code from the static `ErrorCodes` object.
     */
    _handleError(errorCode) {
      this._inputIcon.innerHTML = this._redmarkSvg;
      const { strings } = this._configData;

      const errorMessage = (() => {
        switch (errorCode) {
          case TaxExemptManager.ErrorCodes.VAT_DOES_NOT_EXIST:
            return strings["vat_does_not_exist"];
          case TaxExemptManager.ErrorCodes.VAT_NOT_VALID:
            return strings["vat_not_valid"];
          case TaxExemptManager.ErrorCodes.UNSET_ERROR:
            return strings["vat_deleted_error"];
          default:
            return strings["error_general"];
        }
      })();

      this._renderMessage(errorMessage, "ERROR");
    }

    /**

     * Renders a message in the designated message container with appropriate styling.
     * @private
     * @param {string} msg - The message text to display.
     * @param {'INFO' | 'ERROR' | 'SUCCESS'} [type='INFO'] - The type of message, for styling.
     */
    _renderMessage = (msg, type = "INFO") => {
      this._log("INFO", "Displaying message", type, msg);
      const className = (() => {
        switch (type) {
          case "ERROR":
            return "text-error";
          case "SUCCESS":
            return "text-success";
          default:
            return "";
        }
      })();

      this._message.innerHTML = `<div class="${className}">${msg}</div>`;
    };

    /**
     * Clears any message from the message container.
     * @private
     */
    _clearMessage() {
      this._log("INFO", "Clearing message");
      this._message.innerHTML = "";
    }

    /**
     * Toggles the loading state of a button.
     * @private
     * @param {HTMLElement} btn - The button element.
     * @param {boolean} loading - True to set to loading and disabled state.
     */
    _toggleButtonLoading = (btn, loading) => {
      if (btn) {
        this._log("INFO", "Setting button loading state", btn, loading);
        btn.toggleAttribute("disabled", loading);
        btn.classList.toggle("loading", loading);

        const loadingSpinner = btn.querySelector(".loading__spinner");
        if (loadingSpinner) {
          loadingSpinner.classList.toggle("hidden", !loading);
        }
      }
    };

    /**
     * Updates the value of the hidden input field.
     * This is used to pass the VAT ID along with a form submission.
     * @private
     * @param {string} vatId - The new VAT ID value to set in the hidden input.
     */
    _updateHiddenInput(vatId) {
      if (this._hiddenInputField) {
        this._hiddenInputField.value = vatId;
      }
    }

    //
    // ----------------- UTILITIES & LOGGING -----------------
    //

    /**
     * Logs messages to the console with color-coded type labels.
     * @private
     * @param {'INFO'|'WARNING'|'ERROR'} type - The type of log message.
     * @param {...*} args - The message(s) or object(s) to log.
     */
    _log = (type, ...args) => {
      if (!this._loggingEnabled) return;

      const styles = {
        INFO: "background: #007bff; color: #ffffff; font-weight: bold; padding: 2px 5px; border-radius: 3px;",
        WARNING:
          "background: #ffc107; color: #000000; font-weight: bold; padding: 2px 5px; border-radius: 3px;",
        ERROR:
          "background: #dc3545; color: #ffffff; font-weight: bold; padding: 2px 5px; border-radius: 3px;",
      };

      console.log(`%c[${type}]`, styles[type] || styles.INFO, ...args);
    };
  }

  customElements.define("tax-exempt-manager", TaxExemptManager);
}
